import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================
// Type Definitions
// ============================================

export interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  age: number;
  gender: string;
  phoneNumber?: string;
}

export interface QueueEntry {
  id: string;
  position: number;
  estimatedWaitMinutes: number;
  status: 'waiting' | 'called' | 'in_consultation' | 'completed' | 'abandoned';
  enteredAt: string;
}

export interface ConsultationData {
  id: string;
  roomUrl: string;
  token: string;
  doctorName?: string;
  doctorSpecialty?: string;
  startedAt?: string;
}

// ============================================
// Store Interface
// ============================================

interface PatientState {
  // State
  patient: Patient | null;
  queueEntry: QueueEntry | null;
  consultation: ConsultationData | null;
  locationId: string;

  // Actions
  setPatient: (patient: Patient | null) => void;
  setQueueEntry: (entry: QueueEntry | null) => void;
  setConsultation: (consultation: ConsultationData | null) => void;
  setLocationId: (locationId: string) => void;
  updateQueuePosition: (position: number, estimatedWaitMinutes: number) => void;
  updateQueueStatus: (status: QueueEntry['status']) => void;
  clear: () => void;
}

// ============================================
// Default Location ID
// ============================================

// Get location ID from environment variable or use default
const getDefaultLocationId = (): string => {
  if (typeof window !== 'undefined') {
    // Vite injects env vars at build time, so we access them through import.meta.env
    // However, in the store initialization, we need to be careful about timing
    return 'default-location';
  }
  return 'default-location';
};

// ============================================
// Store Implementation
// ============================================

export const usePatientStore = create<PatientState>()(
  persist(
    (set) => ({
      // Initial state
      patient: null,
      queueEntry: null,
      consultation: null,
      locationId: getDefaultLocationId(),

      // Actions
      setPatient: (patient) => set({ patient }),

      setQueueEntry: (queueEntry) => set({ queueEntry }),

      setConsultation: (consultation) => set({ consultation }),

      setLocationId: (locationId) => set({ locationId }),

      updateQueuePosition: (position, estimatedWaitMinutes) =>
        set((state) => ({
          queueEntry: state.queueEntry
            ? { ...state.queueEntry, position, estimatedWaitMinutes }
            : null,
        })),

      updateQueueStatus: (status) =>
        set((state) => ({
          queueEntry: state.queueEntry
            ? { ...state.queueEntry, status }
            : null,
        })),

      clear: () =>
        set({
          patient: null,
          queueEntry: null,
          consultation: null,
          // Keep locationId as it's kiosk-specific
        }),
    }),
    {
      name: 'patient-storage',
      version: 1,
      partialize: (state) => ({
        patient: state.patient,
        queueEntry: state.queueEntry,
        consultation: state.consultation,
        locationId: state.locationId,
      }),
      // Migration for future schema changes
      migrate: (persistedState: unknown, version: number) => {
        if (version === 0) {
          // Migration from version 0 to 1
          return persistedState as PatientState;
        }
        return persistedState as PatientState;
      },
    }
  )
);

// ============================================
// Selector Hooks (for optimized re-renders)
// ============================================

export const usePatient = () => usePatientStore((state) => state.patient);
export const useQueueEntry = () => usePatientStore((state) => state.queueEntry);
export const useConsultationData = () => usePatientStore((state) => state.consultation);
export const useLocationId = () => usePatientStore((state) => state.locationId);

// ============================================
// Action Hooks (for mutations)
// ============================================

export const usePatientActions = () =>
  usePatientStore((state) => ({
    setPatient: state.setPatient,
    setQueueEntry: state.setQueueEntry,
    setConsultation: state.setConsultation,
    setLocationId: state.setLocationId,
    updateQueuePosition: state.updateQueuePosition,
    updateQueueStatus: state.updateQueueStatus,
    clear: state.clear,
  }));
